import { FaBox, FaCarSide, FaTools } from 'react-icons/fa';
import { Alert, Button, Card, Col, Empty, Row, Space } from 'antd';

import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkorderItems } from '.';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

type Props = {
  onCancel: VoidFunction;
};

function SelectWorkOrderItemType({ onCancel }: Props) {
  const { itemType, setItemType, onNextStep, kit } = useWorkorderItems();

  const hasKitPermission =
    hasPermissions([
      Authorities.KITS_VIEW,
      Authorities.MOVIMENTATION_WORKORDER_KITS_ADD,
    ]) && !kit;

  const hasPartPermission = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_REGISTER_PART,
  ]);

  const hasServicePermission = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_REGISTER_SERVICE,
  ]);

  const showPermissionAlert = ![
    hasServicePermission,
    hasPartPermission,
    hasKitPermission,
  ].every(Boolean);

  const showEmptyOptions = [
    !hasServicePermission,
    !hasPartPermission,
    !hasKitPermission,
  ].every(Boolean);

  return (
    <>
      <Row gutter={[16, 16]} justify="center" style={{ marginTop: '32px' }}>
        {hasPartPermission && (
          <Col span={24} md={hasKitPermission ? 8 : 12} key={'part'}>
            <Card
              className="invoice-create-steps-card"
              hoverable
              data-active={itemType === WorkOrderItemType.Part}
              onClick={() => setItemType(WorkOrderItemType.Part)}
            >
              <Space size="large" direction="horizontal" align="center">
                <FaCarSide size={48} />
                <span style={{ fontSize: '1.5em' }}>Peça</span>
              </Space>
            </Card>
          </Col>
        )}

        {hasServicePermission && (
          <Col span={24} md={hasKitPermission ? 8 : 12} key={'service'}>
            <Card
              className="invoice-create-steps-card"
              hoverable
              data-active={itemType === WorkOrderItemType.Service}
              onClick={() => setItemType(WorkOrderItemType.Service)}
            >
              <Space size="large" direction="horizontal" align="center">
                <FaTools size={48} />
                <span style={{ fontSize: '1.5em' }}>Serviço</span>
              </Space>
            </Card>
          </Col>
        )}

        {hasKitPermission && (
          <Col span={24} md={8} key={'kits'}>
            <Card
              className="invoice-create-steps-card"
              hoverable
              data-active={itemType === WorkOrderItemType.Kit}
              onClick={() => setItemType(WorkOrderItemType.Kit)}
            >
              <Space size="large" direction="horizontal" align="center">
                <FaBox size={48} />
                <span style={{ fontSize: '1.5em' }}>Kit</span>
              </Space>
            </Card>
          </Col>
        )}

        {showEmptyOptions && (
          <Col span={12} key="empty">
            <Empty
              description={
                <>
                  <p style={{ fontSize: '1.2rem' }}>
                    Você não possui permissão para inserir nenhum tipo de item
                    no orçamento.
                  </p>
                  <p>
                    Entre em contato com o administrador da sua oficina e
                    solicite acesso.
                  </p>
                </>
              }
            />
          </Col>
        )}
      </Row>

      {showPermissionAlert && (
        <Alert
          style={{ marginTop: 24 }}
          type="warning"
          description={
            <>
              <strong>Permissões</strong>

              {!hasPartPermission && (
                <div>
                  - Você não tem permissão para registrar <strong>peças</strong>{' '}
                  no orçamento
                </div>
              )}

              {!hasPartPermission && (
                <div>
                  - Você não tem permissão para registrar{' '}
                  <strong>serviços</strong> no orçamento
                </div>
              )}

              {!hasKitPermission && (
                <div>
                  - Você não tem permissão para registrar <strong>kits</strong>{' '}
                  no orçamento
                </div>
              )}
            </>
          }
        />
      )}

      <SpaceForButton justifyContent="end" style={{ marginTop: '30px' }}>
        <Button block onClick={onCancel}>
          Cancelar
        </Button>

        <Button block type="primary" onClick={onNextStep} disabled={!itemType}>
          Próximo
        </Button>
      </SpaceForButton>
    </>
  );
}

export default SelectWorkOrderItemType;
